import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import {
  ExpandMore,
  ArrowBack,
  QuestionAnswer,
  Email,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { trackButtonClick } from "../utils/analytics";

const faqData = [
  {
    question: "💰 Is Dad2Kid really free?",
    answer:
      "Yes! Dad2Kid offers a generous free tier with access to hundreds of activities. You can start using it immediately without any credit card required. Premium features are available for power users who want unlimited access and advanced features.",
    category: "Pricing",
  },
  {
    question: "⏰ How much time do activities take?",
    answer:
      "Activities range from 5-minute quick games to 2-hour adventures. We categorize them by time commitment so you can choose what fits your schedule. Most activities are 15-30 minutes, perfect for busy dads who want quality time without the stress.",
    category: "Activities",
  },
  {
    question: "📱 Can I use Dad2Kid on my phone?",
    answer:
      "Yes! Dad2Kid works perfectly on all devices - phones, tablets, and computers. The mobile app is optimized for on-the-go use, so you can quickly find activities even when you're away from home. All your data syncs across devices.",
    category: "Technology",
  },
  {
    question: "👶 What age groups are the activities for?",
    answer:
      "We have activities for kids from 2 to 12 years old, with age-appropriate suggestions and safety considerations. Each activity clearly shows the recommended age range, and you can filter by your child's age to find perfect matches.",
    category: "Activities",
  },
  {
    question: "🌦️ Do you consider weather in activity suggestions?",
    answer:
      "Absolutely! Dad2Kid checks your local weather and suggests indoor activities on rainy days, outdoor adventures when it's sunny, and seasonal activities throughout the year. You can also manually filter by indoor/outdoor preferences.",
    category: "Features",
  },
  {
    question: "🔒 Is my family's data safe?",
    answer:
      "Your privacy and security are our top priorities. We use bank-level encryption, never share your data with third parties, and are fully COPPA compliant. Your family's information is protected with the same security standards used by major banks.",
    category: "Privacy",
  },
  {
    question: "📧 How do I get support if I need help?",
    answer:
      "We offer multiple ways to get help: in-app chat support, email at support@dad2kid.com, and a comprehensive help center. Our support team typically responds within 2 hours during business hours.",
    category: "Support",
  },
  {
    question: "🎯 How do you personalize activity suggestions?",
    answer:
      "Dad2Kid learns from your family's preferences, your child's age and interests, your available time, and even your location and weather. The more you use it, the better our suggestions become at matching your family's unique needs.",
    category: "Features",
  },
  {
    question: "📱 Can I use Dad2Kid offline?",
    answer:
      "Yes! You can download activities for offline use. This is perfect for camping trips, flights, or anywhere without internet. Simply save your favorite activities and they'll be available even without a connection.",
    category: "Technology",
  },
  {
    question: "🔄 Can I cancel my subscription anytime?",
    answer:
      "Absolutely! You can cancel your premium subscription at any time with no questions asked. Your free account will continue to work, and you'll keep access to all the basic features. No hidden fees or complicated cancellation process.",
    category: "Pricing",
  },
];

const categories = [
  "All",
  "Pricing",
  "Activities",
  "Technology",
  "Features",
  "Privacy",
  "Support",
];

interface FAQPageProps {
  onBack: () => void;
}

const FAQPage: React.FC<FAQPageProps> = ({ onBack }) => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const filteredFAQs =
    selectedCategory === "All"
      ? faqData
      : faqData.filter((faq) => faq.category === selectedCategory);

  const handleContactSupport = () => {
    trackButtonClick("faq_contact_support", {
      button_text: "Contact Support",
      source: "faq_page",
    });
    // This could open a contact form or chat
    window.open("mailto:support@dad2kid.com", "_blank");
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      {/* Header */}
      <Box
        component="header"
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          position: "sticky",
          top: 0,
          zIndex: 1000,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={onBack}
                sx={{ color: "primary.main" }}
              >
                Back
              </Button>
              <Typography
                variant="h5"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <QuestionAnswer />
                Frequently Asked Questions
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 6, pb: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Introduction */}
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 3,
                background: "linear-gradient(45deg, #FF6B6B, #FF8E53)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              }}
            >
              How Can We Help?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "text.secondary",
                maxWidth: 600,
                mx: "auto",
                lineHeight: 1.6,
                fontSize: { xs: "1rem", sm: "1.125rem" },
              }}
            >
              Find answers to the most common questions about Dad2Kid. Can't
              find what you're looking for? Our support team is here to help!
            </Typography>
          </Box>

          {/* Category Filter */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 3,
                textAlign: "center",
              }}
            >
              Filter by Category
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              {categories.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  onClick={() => setSelectedCategory(category)}
                  sx={{
                    bgcolor:
                      selectedCategory === category
                        ? "primary.main"
                        : "grey.100",
                    color:
                      selectedCategory === category ? "white" : "text.primary",
                    fontWeight: 600,
                    "&:hover": {
                      bgcolor:
                        selectedCategory === category
                          ? "primary.dark"
                          : "grey.200",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* FAQ Accordion */}
          <Box sx={{ maxWidth: 800, mx: "auto" }}>
            {filteredFAQs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Accordion
                  expanded={expanded === `panel${index}`}
                  onChange={handleChange(`panel${index}`)}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    "&:before": {
                      display: "none",
                    },
                    "&:hover": {
                      boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      py: 2,
                      px: 3,
                      "& .MuiAccordionSummary-content": {
                        margin: 0,
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#2C3E50",
                        fontSize: { xs: "1rem", sm: "1.125rem" },
                      }}
                    >
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 3, pb: 3 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#666",
                        lineHeight: 1.6,
                        fontSize: { xs: "0.95rem", sm: "1rem" },
                      }}
                    >
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </motion.div>
            ))}
          </Box>

          {/* Contact Support Section */}
          <Box
            sx={{
              textAlign: "center",
              mt: 8,
              p: 4,
              bgcolor: "grey.50",
              borderRadius: 3,
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: "primary.main",
              }}
            >
              Still have questions?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                mb: 3,
                maxWidth: 500,
                mx: "auto",
              }}
            >
              Our support team is here to help you get the most out of Dad2Kid.
              We typically respond within 2 hours during business hours.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<Email />}
              onClick={handleContactSupport}
              sx={{
                bgcolor: "primary.main",
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                "&:hover": {
                  bgcolor: "primary.dark",
                },
              }}
            >
              Contact Support
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default FAQPage;
